import {
  PracticeTemplate,
  PracticeTemplateDetail,
  ProgramBenchmarkName,
  ProgramTask,
  TaskDataTraitStatus,
} from '@cibo/core'
import { useAuth } from '@cibo/profile'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { equals, map, pipe, prop, sort, uniq } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import {
  useCreatePracticeTemplate,
  useDeletePracticeTemplate,
  usePracticeTemplates,
  useUpdatePracticeTemplate,
} from '../../../../../queries'
import { TemplateEditorMain } from './TemplateEditorMain'
import { TemplateEditorSidebar } from './TemplateEditorSidebar'

interface TemplateEditorModalProps {
  open: boolean
  onClose: () => void
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  initialTemplateId?: string
}

export const TemplateEditorModal = ({
  open,
  onClose,
  task,
  initialTemplateId,
}: TemplateEditorModalProps) => {
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')
  const { enqueueSnackbar } = useSnackbar()
  const { userId: authUserId, persona } = useAuth()
  const { engagement, program, fieldModels, userId: farmerUserId } = useProgramEngagementContext()

  const programId = engagement.data?.programId ?? ''
  const taskId = task.id
  const selectedField = fieldModels.data?.items?.[0]

  const benchmark = program.data?.benchmarks?.find(
    b => b.benchmark === task.rule.params?.benchmarkName
  )

  const years: string[] = useMemo(
    () =>
      pipe(
        map(prop('year')),
        // @ts-ignore
        uniq,
        sort((a: number | undefined, b: number | undefined) => (a ?? 0) - (b ?? 0)),
        map(String)
      )(benchmark?.details ?? []) as string[],
    [benchmark?.details]
  )

  const [selectedId, setSelectedId] = useState<string | 'new'>(initialTemplateId ?? 'new')

  useEffect(() => {
    if (open) setSelectedId(initialTemplateId ?? 'new')
  }, [open, initialTemplateId])
  const [draftName, setDraftName] = useState('')
  const [draftDetails, setDraftDetails] = useState<PracticeTemplateDetail[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [ownerScope, setOwnerScope] = useState<'farmer' | 'all'>('farmer')

  //  the logged in user must have an id otherwise more fundamental issues are occurring
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const templateOwner = ownerScope === 'farmer' ? farmerUserId ?? authUserId! : authUserId!
  const [nameError, setNameError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showUnsavedGuard, setShowUnsavedGuard] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<string | 'new' | null>(null)

  // Refs so autosave callbacks always read the freshest values regardless of stale closures
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId
  const draftNameRef = useRef(draftName)
  draftNameRef.current = draftName
  const selectedTemplateRef = useRef<PracticeTemplate | undefined>(undefined)
  const draftDetailsRef = useRef(draftDetails)
  draftDetailsRef.current = draftDetails
  const templateOwnerRef = useRef(templateOwner)
  templateOwnerRef.current = templateOwner
  // Prevents concurrent create requests from handleSave (blur) and autosaveDetails racing
  const isCreatingRef = useRef(false)

  const isAdvisorOrAdmin = persona === 'advisor' || persona === 'admin'
  const ownTemplates = usePracticeTemplates(
    { programId, taskId, owner: authUserId },
    { enabled: open && !!programId && !isAdvisorOrAdmin }
  )
  const allTemplates = usePracticeTemplates(
    { programId, taskId },
    { enabled: open && !!programId && isAdvisorOrAdmin }
  )

  const createTemplate = useCreatePracticeTemplate()
  const updateTemplate = useUpdatePracticeTemplate()
  const deleteTemplate = useDeletePracticeTemplate()

  const sidebarTemplates: PracticeTemplate[] = useMemo(() => {
    if (isAdvisorOrAdmin && allTemplates.data) return allTemplates.data
    return ownTemplates.data ?? []
  }, [isAdvisorOrAdmin, allTemplates.data, ownTemplates.data])

  const isLoadingTemplates = isAdvisorOrAdmin ? allTemplates.isLoading : ownTemplates.isLoading

  const selectedTemplate = sidebarTemplates.find(tmpl => tmpl.id === selectedId)
  selectedTemplateRef.current = selectedTemplate
  const isOwnTemplate = selectedId === 'new' || selectedTemplate?.owner === authUserId
  const canEdit = isOwnTemplate || isAdvisorOrAdmin
  const canDelete =
    selectedId !== 'new' && (isOwnTemplate || persona === 'advisor' || persona === 'admin')

  const availableTraitIds: string[] = useMemo(() => {
    const existing = new Set(draftDetails.map(d => d.traitId))
    return (
      pipe(
        map(prop('traitId')),
        // @ts-ignore
        uniq
      )(benchmark?.details ?? []) as string[]
    ).filter(id => !existing.has(id))
  }, [benchmark?.details, draftDetails])

  const fieldHasData = useMemo(() => {
    if (!selectedField || !benchmark) return false
    return (benchmark.details ?? []).some(
      d => d.year && selectedField.findDetail({ traitId: d.traitId, year: d.year })
    )
  }, [selectedField, benchmark])

  // ── Load selected template into draft ──
  useEffect(() => {
    if (!open) return
    if (selectedId === 'new') {
      setDraftName('')
      setDraftDetails([])
      setIsDirty(false)
      setNameError(null)
    } else if (selectedTemplate) {
      setDraftName(selectedTemplate.name)
      setDraftDetails(selectedTemplate.details)
      setIsDirty(false)
      setNameError(null)
      setOwnerScope(selectedTemplate.owner === farmerUserId ? 'farmer' : 'all')
    }
  }, [selectedId, selectedTemplate?.id, open])

  const requestNavigate = (targetId: string | 'new') => {
    if (isDirty) {
      setPendingNavigation(targetId)
      setShowUnsavedGuard(true)
    } else {
      setSelectedId(targetId)
    }
  }

  const requestClose = () => {
    if (isDirty) {
      setPendingNavigation(null)
      setShowUnsavedGuard(true)
    } else {
      onClose()
    }
  }

  const handleUnsavedDiscard = () => {
    setShowUnsavedGuard(false)
    setIsDirty(false)
    if (pendingNavigation !== null) {
      setSelectedId(pendingNavigation)
      setPendingNavigation(null)
    } else {
      onClose()
    }
  }

  const autosaveDetails = useCallback(
    async (newDetails: PracticeTemplateDetail[]) => {
      const currentSelectedId = selectedIdRef.current
      const trimmedName = draftNameRef.current.trim()
      if (!trimmedName) return

      if (currentSelectedId === 'new') {
        // Defer create until at least one detail has a real value
        if (
          !newDetails.some(
            d => d.value !== undefined || d.input !== undefined || d.result !== undefined
          )
        )
          return
        if (isCreatingRef.current) return
        isCreatingRef.current = true
        try {
          await createTemplate
            .mutateAsync({
              owner: templateOwnerRef.current,
              name: trimmedName,
              programId,
              taskId,
              details: newDetails,
            })
            .then(result => {
              setIsDirty(false)
              setSelectedId(result.item.id)
              return result
            })
        } catch {
          enqueueSnackbar(t('saveError'), { variant: 'error' })
        } finally {
          isCreatingRef.current = false
        }
        return
      }

      const saved = selectedTemplateRef.current
      if (trimmedName === (saved?.name ?? '') && equals(newDetails, saved?.details ?? [])) return
      try {
        await updateTemplate
          .mutateAsync({
            id: currentSelectedId,
            programId,
            taskId,
            owner: templateOwnerRef.current,
            name: trimmedName,
            details: newDetails,
          })
          .then(created => {
            setIsDirty(false)
            return created
          })
      } catch {
        enqueueSnackbar(t('saveError'), { variant: 'error' })
      }
    },
    [createTemplate, updateTemplate, programId, taskId, authUserId, enqueueSnackbar, t]
  )

  const handleValueSave = useCallback(
    (
      traitId: string,
      year: number,
      filledValues: { value?: unknown; input?: unknown; result?: unknown }
    ) => {
      const value = filledValues?.value !== undefined ? { value: filledValues.value } : {}
      const input = filledValues?.input !== undefined ? { input: filledValues.input } : {}
      const result = filledValues?.result !== undefined ? { result: filledValues.result } : {}
      const prev = draftDetailsRef.current
      const idx = prev.findIndex(d => d.traitId === traitId && d.year === year)
      const newDetails =
        idx >= 0
          ? prev.map((d, i) => (i === idx ? { ...d, ...value, ...input, ...result } : d))
          : [...prev, { traitId, year, ...value, ...input, ...result }]
      setDraftDetails(newDetails)
      autosaveDetails(newDetails)
    },
    [autosaveDetails]
  )

  const handleValueDelete = useCallback(
    (traitId: string, year: number) => {
      const newDetails = draftDetailsRef.current.filter(
        d => !(d.traitId === traitId && d.year === year)
      )
      setDraftDetails(newDetails)
      autosaveDetails(newDetails)
    },
    [autosaveDetails]
  )

  const handleAddPractice = useCallback(
    (traitId: string) => {
      const traitYears = (benchmark?.details ?? [])
        .filter(d => d.traitId === traitId && d.year !== undefined)
        .map(d => d.year)
        .filter(Boolean) as number[]
      const newEntries: PracticeTemplateDetail[] = traitYears.map(year => ({
        traitId,
        year,
        value: undefined,
      }))
      const newDetails = [...draftDetailsRef.current, ...newEntries]
      setDraftDetails(newDetails)
      autosaveDetails(newDetails)
    },
    [benchmark?.details, autosaveDetails]
  )

  const handlePrePopulate = useCallback(() => {
    if (!selectedField || !benchmark) return
    const newDetails: PracticeTemplateDetail[] = (benchmark.details ?? [])
      .filter(d => d.year !== undefined)
      .map(d => {
        const fieldDetail = selectedField.findDetail({ traitId: d.traitId, year: d.year })
        if (!fieldDetail) return null
        const value = fieldDetail.value !== undefined ? { value: fieldDetail.value } : {}
        const input = fieldDetail.input !== undefined ? { input: fieldDetail.input } : {}
        const result = fieldDetail.result !== undefined ? { result: fieldDetail.result } : {}
        if (!Object.keys(value).length && !Object.keys(input).length && !Object.keys(result).length)
          return null
        return {
          traitId: d.traitId,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          year: d.year!,
          ...input,
          ...value,
          ...result,
        }
      })
      .filter(Boolean) as PracticeTemplateDetail[]
    setDraftDetails(newDetails)
    autosaveDetails(newDetails)
  }, [selectedField, benchmark, autosaveDetails])

  const handleSave = async () => {
    const trimmedName = draftName.trim()
    if (!trimmedName) {
      setIsDirty(true)
      setNameError(t('nameRequiredError'))
      return
    }

    // Duplicate name check
    const isDuplicate = sidebarTemplates.some(
      tmpl => tmpl.name === trimmedName && (selectedId === 'new' || tmpl.id !== selectedId)
    )
    if (isDuplicate) {
      setNameError(t('duplicateNameError'))
      return
    }
    setNameError(null)

    try {
      if (selectedId === 'new') {
        // If no real values yet, defer creation until autosaveDetails fires on first value entry
        if (!draftDetails.some(d => d.value !== undefined)) return
        if (isCreatingRef.current) return
        isCreatingRef.current = true
        try {
          const created = await createTemplate.mutateAsync({
            owner: templateOwner,
            name: trimmedName,
            programId,
            taskId,
            details: draftDetails,
          })
          setIsDirty(false)
          setSelectedId(created.item.id)
        } finally {
          isCreatingRef.current = false
        }
      } else {
        const saved = selectedTemplate
        if (trimmedName === (saved?.name ?? '') && equals(draftDetails, saved?.details ?? []))
          return
        await updateTemplate.mutateAsync({
          id: selectedId,
          programId,
          taskId,
          owner: templateOwner,
          name: trimmedName,
          details: draftDetails,
        })
        setIsDirty(false)
      }
      enqueueSnackbar(t('saveSuccess'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('saveError'), { variant: 'error' })
    }
  }

  const handleOwnerScopeChange = useCallback(
    async (scope: 'farmer' | 'all') => {
      setOwnerScope(scope)
      if (selectedIdRef.current === 'new') return
      //  the logged in user must have an id otherwise more fundamental issues are occurring
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newOwner = scope === 'farmer' ? farmerUserId ?? authUserId! : authUserId!
      try {
        await updateTemplate.mutateAsync({
          id: selectedIdRef.current,
          programId,
          taskId,
          owner: newOwner,
          name: draftNameRef.current.trim(),
          details: draftDetailsRef.current,
        })
      } catch {
        enqueueSnackbar(t('saveError'), { variant: 'error' })
      }
    },
    [farmerUserId, authUserId, updateTemplate, programId, taskId, enqueueSnackbar, t]
  )

  const handleDeleteConfirm = async () => {
    if (selectedId === 'new') return
    try {
      await deleteTemplate.mutateAsync(selectedId)
      setShowDeleteConfirm(false)
      setSelectedId('new')
      setDraftName('')
      setDraftDetails([])
      setIsDirty(false)
      enqueueSnackbar(t('deleteSuccess'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('deleteError'), { variant: 'error' })
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth="lg"
        fullWidth
        data-testid="template-editor-modal"
      >
        <Stack direction="row" sx={{ height: '80vh', overflow: 'hidden' }}>
          <TemplateEditorSidebar
            templates={sidebarTemplates}
            isLoading={isLoadingTemplates}
            selectedId={selectedId}
            onSelect={requestNavigate}
            draftName={draftName}
            onDraftNameChange={name => {
              setDraftName(name)
              setIsDirty(true)
              setNameError(null)
            }}
            nameError={nameError}
            canEdit={canEdit}
            onSave={handleSave}
            isAdvisorOrAdmin={isAdvisorOrAdmin}
            farmerUserId={farmerUserId}
            authUserId={authUserId}
          />
          <TemplateEditorMain
            task={task}
            draftDetails={draftDetails}
            years={years}
            onValueSave={handleValueSave}
            onValueDelete={handleValueDelete}
            onAddPractice={handleAddPractice}
            onPrePopulate={handlePrePopulate}
            availableTraitIds={availableTraitIds}
            fieldHasData={fieldHasData}
            canEdit={canEdit}
            canDelete={canDelete}
            isDeleting={deleteTemplate.isPending}
            onDelete={() => setShowDeleteConfirm(true)}
            onClose={requestClose}
            ownerScope={ownerScope}
            onOwnerScopeChange={handleOwnerScopeChange}
          />
        </Stack>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <DialogTitle>{t('deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteConfirmMessage', { name: selectedTemplate?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            loading={deleteTemplate.isPending}
            onClick={handleDeleteConfirm}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved changes guard */}
      <Dialog open={showUnsavedGuard} onClose={() => setShowUnsavedGuard(false)}>
        <DialogTitle>{t('unsavedChangesTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('unsavedChangesMessage', { context: !!draftName.trim() ? 'changes' : 'name' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedGuard(false)}>{t('keepEditing')}</Button>
          <Button color="error" onClick={handleUnsavedDiscard}>
            {t('discardChanges', { context: !!draftName.trim() ? 'changes' : 'name' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
